import type {
  AssigneeSummary,
  CustomerSentiment,
  SupportTicketListItemResponse,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import {
  ConversationAssigneeSummary,
  ConversationResolutionSentiment,
} from "@redotech/redo-model/conversation-insights";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  ConversationAssigneeSummaryColorMap,
  ConversationAssigneeSummaryLabels,
  ConversationResolutionSentimentColorMap,
  ConversationResolutionSentimentLabels,
} from "@redotech/redo-web/conversation-insights/conversation-insights-ui";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { CollapsibleSection } from "./collapsible-section";
import { FieldLabel } from "./field-label";

export interface AiInsightsSectionProps {
  conversation: SupportTicketListItemResponse;
}

const SentimentBadge = memo(function SentimentBadge({
  sentiment,
}: {
  sentiment: CustomerSentiment;
}) {
  const sentimentEnum = sentiment as unknown as ConversationResolutionSentiment;
  return (
    <RedoBadge
      color={ConversationResolutionSentimentColorMap[sentimentEnum]}
      size="sm"
      text={ConversationResolutionSentimentLabels[sentimentEnum]}
    />
  );
});

const AssigneeSummaryBadge = memo(function AssigneeSummaryBadge({
  assigneeSummary,
}: {
  assigneeSummary: AssigneeSummary;
}) {
  const assigneeSummaryEnum =
    assigneeSummary as unknown as ConversationAssigneeSummary;
  return (
    <RedoBadge
      color={ConversationAssigneeSummaryColorMap[assigneeSummaryEnum]}
      size="sm"
      text={ConversationAssigneeSummaryLabels[assigneeSummaryEnum]}
    />
  );
});

const formatTimestamp = (date: Date | null): string => {
  if (!date) {
    return "-";
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const AiInsightsSection = memo(function AiInsightsSection({
  conversation,
}: AiInsightsSectionProps) {
  const hasSummaryData =
    conversation.summary ||
    conversation.customerSentiment ||
    conversation.assigneeSummary ||
    conversation.lastCustomerMessageAt ||
    conversation.lastMerchantMessageAt;

  if (!hasSummaryData) {
    return null;
  }

  return (
    <CollapsibleSection
      localStorageKey="redo.support-v2.conversation-details.summary.collapsed"
      title="Conversation"
    >
      {conversation.summary && (
        <Flex dir="column" gap="xs">
          <FieldLabel>Summary</FieldLabel>
          <Text fontSize="xs" textColor="primary">
            {conversation.summary}
          </Text>
        </Flex>
      )}

      {conversation.customerSentiment && (
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Sentiment</FieldLabel>
          <SentimentBadge sentiment={conversation.customerSentiment} />
        </Flex>
      )}

      {conversation.assigneeSummary && (
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Assignee</FieldLabel>
          <AssigneeSummaryBadge
            assigneeSummary={conversation.assigneeSummary}
          />
        </Flex>
      )}

      <Flex align="flex-start" gap="md" justify="space-between">
        <FieldLabel>Last customer message</FieldLabel>
        <Text fontSize="xs" textColor="primary" whiteSpace="nowrap">
          {formatTimestamp(conversation.lastCustomerMessageAt)}
        </Text>
      </Flex>

      <Flex align="flex-start" gap="md" justify="space-between">
        <FieldLabel>Last merchant message</FieldLabel>
        <Text fontSize="xs" textColor="primary" whiteSpace="nowrap">
          {formatTimestamp(conversation.lastMerchantMessageAt)}
        </Text>
      </Flex>
    </CollapsibleSection>
  );
});
